import React from 'react';
import { ArrowRight, CheckCircle2, Wand2, Sparkles, Layers, ArrowLeft } from 'lucide-react';
import PublicHeader from './PublicHeader';
import './landing/luxe/luxe.css';

const FEATURES = [
  {
    icon: Layers,
    title: 'Design edits',
    desc: 'Adjust silhouettes, necklines, sleeves, lengths, and more.',
  },
  {
    icon: Sparkles,
    title: 'Magic Edit',
    desc: 'Describe changes in text and let AI transform the output.',
  },
  {
    icon: Wand2,
    title: 'Variations',
    desc: 'Generate faster iterations to explore creative directions.',
  },
  {
    icon: CheckCircle2,
    title: 'Production-ready',
    desc: 'Finalize concepts with cleaner, studio-grade outputs.',
  },
];

const StudioInfoPage = ({ onBack, onOpenStudio, isAuthenticated, setActiveView, onSupportClick }) => {
  return (
    <div className="luxe-root min-h-screen">
      <PublicHeader
        activeViewId="studio-info"
        setActiveView={setActiveView}
        variant="dark"
        onSupportClick={onSupportClick}
      />

      <section className="relative pt-24 sm:pt-28 pb-20 px-4 sm:px-6 luxe-bg-gradient luxe-grain min-h-screen">
        <div className="luxe-aurora" />
        <div className="relative container mx-auto max-w-6xl">
          <div className="flex items-center justify-between gap-3 mb-10">
            <button type="button" onClick={onBack} className="luxe-btn-ghost !px-4 !py-2 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 backdrop-blur px-4 py-2">
              <Wand2 className="w-4 h-4 text-gold" />
              <span className="text-sm font-semibold text-white/85">Fashdeck Studio</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="relative order-1 lg:order-2">
              <div className="absolute -inset-6 bg-gradient-to-r from-gold/20 to-gold-deep/10 blur-3xl" />
              <div className="relative luxe-glass overflow-hidden">
                <img
                  src="/studio.gif"
                  alt="Fashdeck Studio preview"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <div className="p-5 border-t border-white/10">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-white">Fashdeck Studio</p>
                    <p className="text-xs text-gold/80 uppercase tracking-[0.2em]">Preview</p>
                  </div>
                  <p className="text-sm text-white/50 mt-2 font-light">
                    Import a kit, edit details, and iterate quickly with AI-assisted enhancements.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6 order-2 lg:order-1">
              <p className="luxe-eyebrow">Edit · Enhance · Iterate</p>

              <h1 className="luxe-display text-4xl sm:text-5xl text-white">
                The most advanced AI-first studio for{' '}
                <span className="luxe-gold-text">editing and fashion design</span>.
              </h1>

              <p className="text-base sm:text-lg text-white/55 leading-relaxed max-w-xl font-light">
                Fashdeck Studio helps you take an AI-generated design and turn it into a ready-to-produce concept—
                with fast edits, variations, and realistic fabric visualization.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {FEATURES.map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="luxe-glass p-4 hover:luxe-hairline-gold transition-colors duration-500">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl border border-gold/30 bg-gold/10 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-gold" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-white">{title}</h3>
                        <p className="text-sm text-white/50 mt-1 font-light">{desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                <button type="button" onClick={onOpenStudio} className="luxe-btn-gold group">
                  {isAuthenticated ? 'Open Studio' : 'Open Studio (Login required)'}
                  <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button type="button" onClick={onBack} className="luxe-btn-ghost">
                  Explore Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudioInfoPage;
